<script lang="ts">
  import {
    Box3,
    BoxGeometry,
    Mesh,
    Object3D,
    type Object3DEventMap,
  } from 'three'
  import useObjectQueries from '../../../hooks/useObjectQueries'
  import { updateSelectionBox } from '../../../../../helpers/selection-material'
  import InputLabel from '../InputLabel.svelte'
  import ControlGroupContent from '../../../../../components/control-group/ControlGroupContent.svelte'
  import TranslationInput from '../../../../../components/translation-input/TranslationInput.svelte'
  import { convertToSceneUnit, convertToUserUnit } from '../../../../../helpers/scene-json'
  import { debounce } from 'es-toolkit'
  import i18n from '../../../../../i18n'

  const { object }: { object: Object3D<Object3DEventMap> } = $props()

  const { useObject, invalidateCanvas } = useObjectQueries()
  const data = $derived(useObject(object.uuid))

  function getBoxDimensions(selectedObject?: Object3D) {
    if (!selectedObject) return { width: 0, height: 0, length: 0 }
    const box = new Box3().setFromObject(selectedObject)
    const width = box.max.x - box.min.x
    const height = box.max.z - box.min.z
    const length = box.max.y - box.min.y
    return { width, height, length }
  }

  let { width, height, length } = $derived(getBoxDimensions(data.data))

  const unit = 'mm'

  const throttledInvalidateCanvas = debounce(() => {
    invalidateCanvas()
  }, 3000)

  function handleType(
    newGeometry: Record<'height' | 'width' | 'length', number>,
  ) {
    if (!(data.data instanceof Mesh)) return

    data.data.geometry = new BoxGeometry(
      newGeometry.width,
      newGeometry.length,
      newGeometry.height,
    )

    data.data.geometry.translate(
      newGeometry.width / 2,
      newGeometry.length / 2,
      newGeometry.height / 2,
    )
    updateSelectionBox(data.data)
    throttledInvalidateCanvas()
  }
</script>

{#key data.dataUpdatedAt}
  {#if data.data}
    <div class="control-group-container">
      <ControlGroupContent>
        <InputLabel>{$i18n.t('project.rightBar.size.width')}</InputLabel>
        <TranslationInput
          name="width"
          onarrowpressed={v => handleType({ height, width: convertToSceneUnit(v) as number, length })}
          ontype={v => handleType({ height, width: convertToSceneUnit(v) as number, length })}
          {unit}
          defaultValue={convertToUserUnit(width)}
          onblur={invalidateCanvas}
        />
      </ControlGroupContent>
      <ControlGroupContent>
        <InputLabel>{$i18n.t('project.rightBar.size.length')}</InputLabel>
        <TranslationInput
          name="length"
          onarrowpressed={v => handleType({ height, width, length: convertToSceneUnit(v) as number })}
          ontype={v => handleType({ height, width, length: convertToSceneUnit(v) as number })}
          {unit}
          defaultValue={convertToUserUnit(length)}
          onblur={invalidateCanvas}
        />
      </ControlGroupContent>
      <ControlGroupContent>
        <InputLabel>{$i18n.t('project.rightBar.size.height')}</InputLabel>
        <TranslationInput
          name="height"
          onarrowpressed={v => handleType({ height: convertToSceneUnit(v) as number, width, length })}
          ontype={v => handleType({ height: convertToSceneUnit(v) as number, width, length })}
          {unit}
          defaultValue={convertToUserUnit(height)}
          onblur={invalidateCanvas}
        />
      </ControlGroupContent>
    </div>
  {/if}
{/key}

<style lang="scss">
  .control-group-container {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }
  </style>
